// Copyright (c) 2023 MOCA Systems, Inc. All rights reserved.

import { Injectable } from '@angular/core';
import {
	HttpInterceptor,
	HttpEvent,
	HttpRequest,
	HttpHandler,
	HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { LoggerServiceFactory } from '../../common/logging/LoggerServiceFactory';
import { ProcoreLoginStateService } from '../procore-login-state.service';
import { PROCORE_BASE_ROUTE } from './rest-api-routes';

/**
 * This class implements a HttpInterceptor that intercepts the Procore requests to handle authentication.
 * Also it provides a retry strategy for Procore requests to handle token expiration.
 */
@Injectable()
export class ProcoreInterceptor implements HttpInterceptor {
	private logger = LoggerServiceFactory.create('ProcoreInterceptor');

	constructor(private procoreLoginStateService: ProcoreLoginStateService) {}

	public intercept(httpRequest: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// Apply the interceptor only to Procore URLs
		// Also skip if it is Procore Authentication URL
		if (
			!httpRequest.url.includes(PROCORE_BASE_ROUTE) ||
			(httpRequest.url.includes(PROCORE_BASE_ROUTE) && this.isAuthUrl(httpRequest.url))
		) {
			return next.handle(httpRequest);
		}
		// Log http call in console
		this.logger.log(httpRequest.url);
		// get current user authorization token
		return this.handleProcoreRequest$(httpRequest, next);
	}

	// this method us used to add Procore authentication token to all non-authentication Procore requests.
	// also it implements a retry strategy when Procore token is expired and it needs to be refreshed
	private handleProcoreRequest$(httpRequest: HttpRequest<any>, next: HttpHandler) {
		return this.procoreLoginStateService.token$.pipe(
			take(1),
			switchMap((accessToken) => {
				if (!accessToken) {
					return this.authenticateAndRetry$(httpRequest, next);
				}
				const reqClone = httpRequest.clone({
					// Procore authenticated requests expect the Procore access token in the body payload.
					body: { ...httpRequest.body, accessToken },
				});
				return next.handle(reqClone).pipe(
					catchError((error: HttpErrorResponse) => {
						if (error.status === 401) {
							// Authentication error must be catch to retry request once user logged in
							this.logger.warn('expected 401 authentication error, retrying...', error);
							return this.authenticateAndRetry$(httpRequest, next);
						}
						return throwError(error);
					})
				);
			})
		);
	}

	private authenticateAndRetry$(
		httpRequest: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		this.procoreLoginStateService.authenticate({ allowRedirect: false, silentMode: true });
		return this.procoreLoginStateService.token$.pipe(
			filter((token) => !!token),
			take(1),
			switchMap((accessToken) => {
				// Procore authenticated requests expect the Procore access token in the body payload.
				const reqClone = httpRequest.clone({
					body: { ...httpRequest.body, accessToken },
				});
				return next.handle(reqClone);
			})
		);
	}

	private isAuthUrl(url: string): boolean {
		const urlSegments = url.split('/');
		return urlSegments.some((segment) => segment === 'auth');
	}
}
